import React, { useState, useEffect, type FC } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedAccountNumber,
  MaskedValue,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { AlertTriangle } from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { useCitation, getCitationForField } from "./CitationContext";
import { cn } from "@/shared/utils/core";
import { validateTrust } from "@/shared/utils/trustValidation";

export interface TrustExtraction {
  trust_information?: {
    trust_name?: string;
    trust_type?: string;
    date_created?: string;
    date_created_raw?: string;
    state_created?: string;
    governing_law_state?: string;
    grantor_name?: string;
    grantor_name_as_printed?: string;
    trust_ein?: string;
  };
  trustees?: Array<{
    name?: string;
    type?: string;
    role?: string;
    is_primary?: boolean;
    relationship?: string;
    co_trustee_terms?: "joint" | "independent" | "majority";
    bond_waiver?: "waived" | "required" | "silent";
    power_to_sell_real_property?: boolean;
  }>;
  successor_trustees?: Array<{
    name?: string;
    order?: number;
    type?: string;
    relationship?: string;
  }>;
  nominated_trustees?: Array<{
    name?: string;
    context?: string;
    type?: "individual" | "corporate";
    relationship?: string;
  }>;
  trustee_succession_terms?: {
    trigger?: string;
    clause_text?: string;
  };
  beneficiaries?: Array<{
    name?: string;
    beneficiary_type?: string;
    relationship?: string;
    percentage?: number;
    is_income_beneficiary?: boolean;
    is_principal_beneficiary?: boolean;
    distribution_conditions?: string;
    bequest_description?: string;
    organization_type?: "religious" | "educational" | "medical" | "civic" | "other";
  }>;
  distribution?: {
    distribution_scheme?: "per_stirpes" | "per_capita" | "per_capita_at_each_generation" | "other";
    distribution_timing?: string;
    survivorship_period?: string;
  };
  sub_trusts?: Array<{
    name?: string;
    beneficiaries?: Array<{ name: string; relationship?: string }>;
    funding_formula?: string;
  }>;
  trust_assets?: Array<{
    asset_description?: string;
    asset_type?: string;
    asset_category?: string;
    estimated_value?: number;
    property_address?: string;
    account_number?: string;
    institution_name?: string;
  }>;
  trust_provisions?: {
    has_spendthrift_clause?: boolean;
    has_special_needs_provisions?: boolean;
    distribution_standard?: string;
    termination_conditions?: string;
    amendment_provisions?: string;
    debt_expense_payment_direction?: string;
    tax_apportionment_clause?: { has_clause?: boolean; description?: string };
    no_contest_clause?: { has_clause?: boolean; clause_text?: string };
  };
  pour_over_will?: {
    has_pour_over_will?: boolean;
    will_date?: string;
    testator_name?: string;
  };
  amendments?: Array<{
    amendment_detected?: boolean;
    amendment_date?: string;
    amendment_scope?: "TRUSTEE_CHANGE" | "BENEFICIARY_CHANGE" | "PROPERTY_CHANGE" | "RESTATEMENT" | "OTHER";
    description?: string;
  }>;
}

const TRUST_ASSET_CATEGORY_CONFIG: Record<string, { label: string; className: string }> = {
  REAL_PROPERTY: { label: "Real property", className: "bg-blue-50 text-blue-700 border-blue-200" },
  FINANCIAL_ACCOUNT: { label: "Financial account", className: "bg-green-50 text-green-700 border-green-200" },
  BUSINESS_INTEREST: { label: "Business interest", className: "bg-purple-50 text-purple-700 border-purple-200" },
  PERSONAL_PROPERTY: { label: "Personal property", className: "bg-amber-50 text-amber-700 border-amber-200" },
  OTHER: { label: "Other", className: "bg-stone-50 text-stone-700 border-stone-200" },
  real_estate: { label: "Real estate", className: "bg-blue-50 text-blue-700 border-blue-200" },
  bank_account: { label: "Bank account", className: "bg-green-50 text-green-700 border-green-200" },
  investment_account: { label: "Investment", className: "bg-green-50 text-green-700 border-green-200" },
  brokerage: { label: "Brokerage", className: "bg-green-50 text-green-700 border-green-200" },
  life_insurance: { label: "Life insurance", className: "bg-teal-50 text-teal-700 border-teal-200" },
  business_interest: { label: "Business interest", className: "bg-purple-50 text-purple-700 border-purple-200" },
  personal_property: { label: "Personal property", className: "bg-amber-50 text-amber-700 border-amber-200" },
  vehicle: { label: "Vehicle", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  other: { label: "Other", className: "bg-stone-50 text-stone-700 border-stone-200" },
};

const trustTypeDisplay = (type?: string) => {
  switch (type) {
    case "REVOCABLE_LIVING":
    case "Revocable":
      return { label: "Revocable living", className: "bg-teal-50 text-teal-700 border-teal-200" };
    case "IRREVOCABLE":
    case "Irrevocable":
      return { label: "Irrevocable", className: "bg-amber-50 text-amber-700 border-amber-200" };
    case "UNCLEAR":
      return { label: "Unclear", className: "bg-stone-50 text-stone-700 border-stone-200" };
    default:
      return type ? { label: type, className: "bg-stone-50 text-stone-700 border-stone-200" } : null;
  }
};

const formatAmendmentScope = (scope?: string) => {
  const labels: Record<string, string> = {
    TRUSTEE_CHANGE: "Trustee change",
    BENEFICIARY_CHANGE: "Beneficiary change",
    PROPERTY_CHANGE: "Property change",
    RESTATEMENT: "Restatement",
    OTHER: "Other",
  };
  return scope ? (labels[scope] ?? scope) : null;
};

const CO_TRUSTEE_TERM_LABELS: Record<string, string> = {
  joint: "Must act jointly",
  independent: "May act independently",
  majority: "Majority rules",
};

const BOND_WAIVER_LABELS: Record<string, string> = {
  waived: "Waived",
  required: "Required",
  silent: "Not addressed",
};

const formatDistributionScheme = (scheme?: string) => {
  const labels: Record<string, string> = {
    per_stirpes: "Per stirpes",
    per_capita: "Per capita",
    per_capita_at_each_generation: "Per capita at each generation",
    other: "Other",
  };
  return scheme ? (labels[scheme] ?? scheme) : null;
};

const BENEFICIARY_GROUP_LABELS: Record<string, string> = {
  primary: "Primary beneficiaries",
  contingent: "Contingent beneficiaries",
  remainder: "Remainder beneficiaries",
  charitable: "Charitable beneficiaries",
};

const BENEFICIARY_GROUP_ORDER = ["primary", "contingent", "remainder", "charitable"];

function ValidationWarnings({ warnings }: { warnings: { message: string; severity: "warning" | "error" | "info" }[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-2 px-4 py-3">
      {warnings.map((w, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 text-sm rounded-md p-2 ${
            w.severity === "error"
              ? "bg-red-50 text-red-700"
              : w.severity === "warning"
                ? "bg-yellow-50 text-yellow-700"
                : "bg-blue-50 text-blue-700"
          }`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{w.message}</span>
        </div>
      ))}
    </div>
  );
}

interface TrustInsightsProps {
  data: TrustExtraction;
  citations?: Record<string, unknown> | null;
}

export const TrustInsights: FC<TrustInsightsProps> = ({ data, citations }) => {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const {
    trust_information,
    trustees,
    successor_trustees,
    nominated_trustees,
    trustee_succession_terms,
    beneficiaries,
    distribution,
    sub_trusts,
    trust_assets,
    trust_provisions,
    pour_over_will,
    amendments,
  } = data;

  const { person } = useAppContext();

  const [decryptedAccountNumbers, setDecryptedAccountNumbers] = useState<
    Record<number, string | null>
  >({});
  const [loadingAccountNumbers, setLoadingAccountNumbers] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!trust_assets || !person?.estate_id) return;

    trust_assets.forEach((asset, index) => {
      if (asset.account_number && !decryptedAccountNumbers[index]) {
        setLoadingAccountNumbers((prev) => ({ ...prev, [index]: true }));

        decryptPersonData(person.estate_id, asset.account_number, "account_number")
          .then((decrypted) => {
            setDecryptedAccountNumbers((prev) => ({
              ...prev,
              [index]: decrypted,
            }));
          })
          .catch((error) => {
            console.error("Failed to decrypt account number:", error);
            setDecryptedAccountNumbers((prev) => ({ ...prev, [index]: null }));
          })
          .finally(() => {
            setLoadingAccountNumbers((prev) => ({ ...prev, [index]: false }));
          });
      }
    });
  }, [trust_assets, person?.estate_id, decryptedAccountNumbers]);

  const govLaw = trust_information?.governing_law_state ?? trust_information?.state_created ?? null;
  const grantor =
    trust_information?.grantor_name_as_printed ?? trust_information?.grantor_name ?? null;

  const hasTrustDetails = !!(
    trust_information?.trust_name ||
    trust_information?.trust_type ||
    trust_information?.date_created ||
    govLaw ||
    grantor ||
    trust_information?.trust_ein
  );
  const hasTrustees = !!(
    trustees?.length ||
    successor_trustees?.length ||
    nominated_trustees?.length ||
    trustee_succession_terms?.trigger ||
    trustee_succession_terms?.clause_text
  );
  const hasBeneficiaries = !!(beneficiaries?.length);
  const hasDistribution = !!(
    distribution?.distribution_scheme ||
    distribution?.distribution_timing ||
    distribution?.survivorship_period
  );
  const hasSubTrusts = !!(sub_trusts?.length);
  const hasAssets = !!(trust_assets?.length);
  const hasProvisions = !!(
    trust_provisions &&
    (trust_provisions.has_spendthrift_clause != null ||
      trust_provisions.has_special_needs_provisions != null ||
      trust_provisions.distribution_standard ||
      trust_provisions.termination_conditions ||
      trust_provisions.amendment_provisions ||
      trust_provisions.debt_expense_payment_direction ||
      trust_provisions.tax_apportionment_clause?.has_clause === true ||
      trust_provisions.tax_apportionment_clause?.description ||
      trust_provisions.no_contest_clause?.has_clause === true ||
      trust_provisions.no_contest_clause?.clause_text)
  );
  const hasPourOver = !!(pour_over_will?.has_pour_over_will);
  const hasAmendments = !!(
    amendments?.some(
      (a) =>
        a.amendment_detected !== false &&
        (a.amendment_date || a.amendment_scope || a.description),
    )
  );

  const warnings = validateTrust(data, person?.full_name, null, person?.date_of_death);
  const warningsForSection = (section: string) =>
    warnings.filter((w) => w.section === section);

  const typeDisplay = trustTypeDisplay(trust_information?.trust_type);

  const beneficiaryGroups = BENEFICIARY_GROUP_ORDER.map((type) => ({
    type,
    label: BENEFICIARY_GROUP_LABELS[type],
    items: (beneficiaries ?? []).filter((b) => b.beneficiary_type === type),
  })).filter((g) => g.items.length > 0);

  const ungroupedBeneficiaries = (beneficiaries ?? []).filter(
    (b) => !b.beneficiary_type || !BENEFICIARY_GROUP_ORDER.includes(b.beneficiary_type),
  );

  return (
    <div className="px-6 pb-6 space-y-4">
      {hasTrustDetails && (
        <InsightsCard title="Trust details" noPadding>
          <ValidationWarnings warnings={warningsForSection("trust_information")} />
          <InsightsDividerList>
            {trust_information?.trust_name && (
              <InsightsDividerRow
                label="Trust name"
                value={trust_information.trust_name}
                onClick={
                  getCitationForField(citations, "trust_information.trust_name")
                    ? () => handleFieldClick("trust_information.trust_name")
                    : undefined
                }
              />
            )}
            {typeDisplay && (
              <InsightsDividerRow
                label="Trust type"
                value={
                  <Badge variant="outline" className={typeDisplay.className}>
                    {typeDisplay.label}
                  </Badge>
                }
                onClick={
                  getCitationForField(citations, "trust_information.trust_type")
                    ? () => handleFieldClick("trust_information.trust_type")
                    : undefined
                }
              />
            )}
            {trust_information?.date_created && (
              <InsightsDividerRow
                label="Date created"
                value={formatDate(trust_information.date_created)}
                onClick={
                  getCitationForField(citations, "trust_information.date_created")
                    ? () => handleFieldClick("trust_information.date_created")
                    : undefined
                }
              />
            )}
            {govLaw && (
              <InsightsDividerRow
                label="Governing law"
                value={govLaw}
                onClick={
                  getCitationForField(
                    citations,
                    trust_information?.governing_law_state
                      ? "trust_information.governing_law_state"
                      : "trust_information.state_created",
                  )
                    ? () =>
                        handleFieldClick(
                          trust_information?.governing_law_state
                            ? "trust_information.governing_law_state"
                            : "trust_information.state_created",
                        )
                    : undefined
                }
              />
            )}
            {grantor && (
              <InsightsDividerRow
                label="Grantor"
                value={grantor}
                onClick={
                  getCitationForField(
                    citations,
                    trust_information?.grantor_name_as_printed
                      ? "trust_information.grantor_name_as_printed"
                      : "trust_information.grantor_name",
                  )
                    ? () =>
                        handleFieldClick(
                          trust_information?.grantor_name_as_printed
                            ? "trust_information.grantor_name_as_printed"
                            : "trust_information.grantor_name",
                        )
                    : undefined
                }
              />
            )}
            {trust_information?.trust_ein && (
              <InsightsDividerRow
                label="Trust EIN"
                value={<MaskedValue value={trust_information.trust_ein} format="ein" />}
                onClick={
                  getCitationForField(citations, "trust_information.trust_ein")
                    ? () => handleFieldClick("trust_information.trust_ein")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasTrustees && (
        <InsightsCard title="Trustees" noPadding>
          <InsightsDividerList>
            {trustees && trustees.length > 0 && (
              <>
                <div className="py-2.5 px-4 bg-subtle">
                  <span className="text-sm font-medium text-secondary">Acting trustees</span>
                </div>
                {trustees.map((trustee, index) => (
                  <div
                    key={`trustee-${index}`}
                    className={cn(
                      "py-3 px-4 space-y-1",
                      getCitationForField(citations, `trustees.${index}.name`)
                        ? "cursor-pointer hover:bg-subtle transition-colors"
                        : "",
                    )}
                    onClick={
                      getCitationForField(citations, `trustees.${index}.name`)
                        ? () => handleFieldClick(`trustees.${index}.name`)
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary inline-flex items-center">
                        {trustee.name || "Unnamed trustee"}
                        {getCitationForField(citations, `trustees.${index}.name`) && (
                          <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                        )}
                      </span>
                      {trustee.is_primary && (
                        <Badge variant="outline" className="bg-subtle text-secondary border-primary text-xs">
                          Primary
                        </Badge>
                      )}
                    </div>
                    {trustee.relationship && trustee.relationship !== "self" && (
                      <p className="text-sm text-secondary">{trustee.relationship}</p>
                    )}
                    {trustee.co_trustee_terms && (
                      <p className="text-sm text-secondary">
                        Co-trustee terms:{" "}
                        {CO_TRUSTEE_TERM_LABELS[trustee.co_trustee_terms] ?? trustee.co_trustee_terms}
                      </p>
                    )}
                    {trustee.bond_waiver && (
                      <p className="text-sm text-secondary">
                        Bond: {BOND_WAIVER_LABELS[trustee.bond_waiver] ?? trustee.bond_waiver}
                      </p>
                    )}
                    {trustee.power_to_sell_real_property != null && (
                      <p className="text-sm text-secondary">
                        Power to sell real property:{" "}
                        {trustee.power_to_sell_real_property ? "Yes" : "No"}
                      </p>
                    )}
                  </div>
                ))}
              </>
            )}

            {successor_trustees && successor_trustees.length > 0 && (
              <>
                <div className="py-2.5 px-4 bg-subtle">
                  <span className="text-sm font-medium text-secondary">Successor trustees</span>
                </div>
                {successor_trustees
                  .slice()
                  .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
                  .map((trustee, index) => (
                    <InsightsDividerRow
                      key={`successor-${index}`}
                      label={trustee.order ? `Successor #${trustee.order}` : "Successor"}
                      value={
                        [trustee.name, trustee.relationship ? `(${trustee.relationship})` : null]
                          .filter(Boolean)
                          .join(" ") || "Unnamed"
                      }
                      onClick={
                        getCitationForField(citations, `successor_trustees.${index}.name`)
                          ? () => handleFieldClick(`successor_trustees.${index}.name`)
                          : undefined
                      }
                    />
                  ))}
              </>
            )}

            {nominated_trustees && nominated_trustees.length > 0 && (
              <>
                <div className="py-2.5 px-4 bg-subtle">
                  <span className="text-sm font-medium text-secondary">Nominated trustees</span>
                </div>
                {nominated_trustees.map((trustee, index) => (
                  <div
                    key={`nominated-${index}`}
                    className={cn(
                      "py-3 px-4 space-y-1",
                      getCitationForField(citations, `nominated_trustees.${index}.name`)
                        ? "cursor-pointer hover:bg-subtle transition-colors"
                        : "",
                    )}
                    onClick={
                      getCitationForField(citations, `nominated_trustees.${index}.name`)
                        ? () => handleFieldClick(`nominated_trustees.${index}.name`)
                        : undefined
                    }
                  >
                    <span className="text-sm font-medium text-primary inline-flex items-center">
                      {trustee.name || "Unnamed"}
                      {getCitationForField(citations, `nominated_trustees.${index}.name`) && (
                        <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                      )}
                    </span>
                    {trustee.type && (
                      <p className="text-sm text-secondary capitalize">{trustee.type}</p>
                    )}
                    {trustee.context && (
                      <p className="text-sm text-secondary">{trustee.context}</p>
                    )}
                  </div>
                ))}
              </>
            )}

            {trustee_succession_terms && (
              trustee_succession_terms.trigger || trustee_succession_terms.clause_text
            ) && (
              <>
                <div className="py-2.5 px-4 bg-subtle">
                  <span className="text-sm font-medium text-secondary">Succession terms</span>
                </div>
                {trustee_succession_terms.trigger && (
                  <InsightsDividerRow
                    label="Trigger"
                    value={trustee_succession_terms.trigger}
                  />
                )}
                {trustee_succession_terms.clause_text && (
                  <div className="py-3 px-4">
                    <p className="text-sm text-secondary mb-1">Clause</p>
                    <p className="text-sm text-primary">{trustee_succession_terms.clause_text}</p>
                  </div>
                )}
              </>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasBeneficiaries && (
        <InsightsCard title="Beneficiaries" noPadding>
          <InsightsDividerList>
            {beneficiaryGroups.map((group) => (
              <React.Fragment key={`group-${group.type}`}>
                <div className="py-2.5 px-4 bg-subtle">
                  <span className="text-sm font-medium text-stone-700">{group.label}</span>
                </div>
                {group.items.map((beneficiary) => {
                  const origIndex = (beneficiaries ?? []).indexOf(beneficiary);
                  return (
                    <div
                      key={`ben-${origIndex}`}
                      className={cn(
                        "py-3 px-4 space-y-1",
                        getCitationForField(citations, `beneficiaries.${origIndex}.name`)
                          ? "cursor-pointer hover:bg-subtle transition-colors"
                          : "",
                      )}
                      onClick={
                        getCitationForField(citations, `beneficiaries.${origIndex}.name`)
                          ? () => handleFieldClick(`beneficiaries.${origIndex}.name`)
                          : undefined
                      }
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-primary inline-flex items-center">
                          {beneficiary.name || "Unnamed"}
                          {getCitationForField(citations, `beneficiaries.${origIndex}.name`) && (
                            <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                          )}
                        </span>
                        {beneficiary.percentage != null && (
                          <span className="text-sm text-primary">{beneficiary.percentage}%</span>
                        )}
                      </div>
                      {beneficiary.relationship && (
                        <p className="text-sm text-secondary">{beneficiary.relationship}</p>
                      )}
                      {beneficiary.distribution_conditions && (
                        <p className="text-sm text-secondary">
                          {beneficiary.distribution_conditions}
                        </p>
                      )}
                      {beneficiary.bequest_description && (
                        <p className="text-sm text-secondary">{beneficiary.bequest_description}</p>
                      )}
                      {beneficiary.organization_type && (
                        <p className="text-sm text-secondary capitalize">
                          {beneficiary.organization_type}
                        </p>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {ungroupedBeneficiaries.map((beneficiary) => {
              const origIndex = (beneficiaries ?? []).indexOf(beneficiary);
              return (
                <div
                  key={`ben-other-${origIndex}`}
                  className={cn(
                    "py-3 px-4 space-y-1",
                    getCitationForField(citations, `beneficiaries.${origIndex}.name`)
                      ? "cursor-pointer hover:bg-subtle transition-colors"
                      : "",
                  )}
                  onClick={
                    getCitationForField(citations, `beneficiaries.${origIndex}.name`)
                      ? () => handleFieldClick(`beneficiaries.${origIndex}.name`)
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary inline-flex items-center">
                      {beneficiary.name || "Unnamed"}
                      {getCitationForField(citations, `beneficiaries.${origIndex}.name`) && (
                        <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                      )}
                    </span>
                    {beneficiary.percentage != null && (
                      <span className="text-sm text-primary">{beneficiary.percentage}%</span>
                    )}
                  </div>
                  {beneficiary.relationship && (
                    <p className="text-sm text-secondary">{beneficiary.relationship}</p>
                  )}
                  {beneficiary.distribution_conditions && (
                    <p className="text-sm text-secondary">{beneficiary.distribution_conditions}</p>
                  )}
                  {beneficiary.bequest_description && (
                    <p className="text-sm text-secondary">{beneficiary.bequest_description}</p>
                  )}
                </div>
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasDistribution && (
        <InsightsCard title="Distribution" noPadding>
          <InsightsDividerList>
            {distribution?.distribution_scheme && (
              <InsightsDividerRow
                label="Distribution scheme"
                value={formatDistributionScheme(distribution.distribution_scheme) ?? ""}
              />
            )}
            {distribution?.distribution_timing && (
              <InsightsDividerRow
                label="Distribution timing"
                value={distribution.distribution_timing}
              />
            )}
            {distribution?.survivorship_period && (
              <InsightsDividerRow
                label="Survivorship period"
                value={distribution.survivorship_period}
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasSubTrusts && (
        <InsightsCard title="Sub-trusts" noPadding>
          <ValidationWarnings warnings={warningsForSection("sub_trusts")} />
          <div className="divide-y divide-stone-100">
            {(sub_trusts ?? []).map((subTrust, index) => (
              <div key={`subtrust-${index}`} className="py-3 px-4 space-y-1.5">
                <p className="text-sm font-medium text-primary">
                  {subTrust.name || "Unnamed sub-trust"}
                </p>
                {subTrust.beneficiaries && subTrust.beneficiaries.length > 0 && (
                  <p className="text-sm text-secondary">
                    Beneficiaries:{" "}
                    {subTrust.beneficiaries
                      .map((b) =>
                        b.relationship ? `${b.name} (${b.relationship})` : b.name,
                      )
                      .join(", ")}
                  </p>
                )}
                {subTrust.funding_formula && (
                  <p className="text-sm text-secondary">Funding: {subTrust.funding_formula}</p>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}

      {hasAssets && (
        <InsightsCard title="Trust assets" noPadding>
          <InsightsDividerList>
            {(trust_assets ?? []).map((asset, index) => {
              const cat = asset.asset_category ?? asset.asset_type ?? null;
              const catConfig = cat ? TRUST_ASSET_CATEGORY_CONFIG[cat] : null;
              return (
                <div
                  key={`asset-${index}`}
                  className={cn(
                    "py-3 px-4 space-y-2",
                    getCitationForField(citations, `trust_assets.${index}.asset_description`)
                      ? "cursor-pointer hover:bg-subtle transition-colors"
                      : "",
                  )}
                  onClick={
                    getCitationForField(citations, `trust_assets.${index}.asset_description`)
                      ? () => handleFieldClick(`trust_assets.${index}.asset_description`)
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary inline-flex items-center">
                      {asset.asset_description || (catConfig?.label ?? "Asset")}
                      {getCitationForField(
                        citations,
                        `trust_assets.${index}.asset_description`,
                      ) && (
                        <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />
                      )}
                    </span>
                    {catConfig && (
                      <Badge variant="outline" className={catConfig.className}>
                        {catConfig.label}
                      </Badge>
                    )}
                  </div>
                  {asset.institution_name && (
                    <div className="text-sm text-secondary">{asset.institution_name}</div>
                  )}
                  {asset.property_address && (
                    <div className="text-sm text-secondary">{asset.property_address}</div>
                  )}
                  {asset.account_number && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-secondary">Account:</span>
                      <MaskedAccountNumber
                        value={decryptedAccountNumbers[index]}
                        isLoading={loadingAccountNumbers[index]}
                      />
                    </div>
                  )}
                  {asset.estimated_value != null && (
                    <div className="text-sm text-secondary">
                      Value: {formatCurrency(asset.estimated_value)}
                    </div>
                  )}
                </div>
              );
            })}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasProvisions && (
        <InsightsCard title="Clauses and provisions" noPadding>
          <InsightsDividerList>
            {trust_provisions?.has_spendthrift_clause != null && (
              <InsightsDividerRow
                label="Spendthrift clause"
                value={trust_provisions.has_spendthrift_clause ? "Yes" : "No"}
                onClick={
                  getCitationForField(citations, "trust_provisions.has_spendthrift_clause")
                    ? () => handleFieldClick("trust_provisions.has_spendthrift_clause")
                    : undefined
                }
              />
            )}
            {trust_provisions?.has_special_needs_provisions != null && (
              <InsightsDividerRow
                label="Special needs provisions"
                value={trust_provisions.has_special_needs_provisions ? "Yes" : "No"}
                onClick={
                  getCitationForField(citations, "trust_provisions.has_special_needs_provisions")
                    ? () => handleFieldClick("trust_provisions.has_special_needs_provisions")
                    : undefined
                }
              />
            )}
            {trust_provisions?.distribution_standard && (
              <InsightsDividerRow
                label="Distribution standard"
                value={trust_provisions.distribution_standard}
                onClick={
                  getCitationForField(citations, "trust_provisions.distribution_standard")
                    ? () => handleFieldClick("trust_provisions.distribution_standard")
                    : undefined
                }
              />
            )}
            {trust_provisions?.termination_conditions && (
              <InsightsDividerRow
                label="Termination conditions"
                value={trust_provisions.termination_conditions}
                onClick={
                  getCitationForField(citations, "trust_provisions.termination_conditions")
                    ? () => handleFieldClick("trust_provisions.termination_conditions")
                    : undefined
                }
              />
            )}
            {trust_provisions?.amendment_provisions && (
              <InsightsDividerRow
                label="Amendment provisions"
                value={trust_provisions.amendment_provisions}
                onClick={
                  getCitationForField(citations, "trust_provisions.amendment_provisions")
                    ? () => handleFieldClick("trust_provisions.amendment_provisions")
                    : undefined
                }
              />
            )}
            {trust_provisions?.debt_expense_payment_direction && (
              <InsightsDividerRow
                label="Debt and expense payment"
                value={trust_provisions.debt_expense_payment_direction}
                onClick={
                  getCitationForField(
                    citations,
                    "trust_provisions.debt_expense_payment_direction",
                  )
                    ? () =>
                        handleFieldClick("trust_provisions.debt_expense_payment_direction")
                    : undefined
                }
              />
            )}
            {trust_provisions?.tax_apportionment_clause?.has_clause && (
              <InsightsDividerRow
                label="Tax apportionment clause"
                value="Yes"
                onClick={
                  getCitationForField(
                    citations,
                    "trust_provisions.tax_apportionment_clause.has_clause",
                  )
                    ? () =>
                        handleFieldClick(
                          "trust_provisions.tax_apportionment_clause.has_clause",
                        )
                    : undefined
                }
              />
            )}
            {trust_provisions?.tax_apportionment_clause?.description && (
              <div className="py-3 px-4">
                <p className="text-sm text-secondary mb-1">Tax apportionment details</p>
                <p className="text-sm text-primary">
                  {trust_provisions.tax_apportionment_clause.description}
                </p>
              </div>
            )}
            {trust_provisions?.no_contest_clause?.has_clause && (
              <InsightsDividerRow
                label="No-contest clause"
                value="Yes"
                onClick={
                  getCitationForField(
                    citations,
                    "trust_provisions.no_contest_clause.has_clause",
                  )
                    ? () =>
                        handleFieldClick("trust_provisions.no_contest_clause.has_clause")
                    : undefined
                }
              />
            )}
            {trust_provisions?.no_contest_clause?.clause_text && (
              <div className="py-3 px-4">
                <p className="text-sm text-secondary mb-1">No-contest clause text</p>
                <p className="text-sm text-primary">
                  {trust_provisions.no_contest_clause.clause_text}
                </p>
              </div>
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasPourOver && (
        <InsightsCard title="Pour-over will" noPadding>
          <ValidationWarnings warnings={warningsForSection("pour_over_will")} />
          <InsightsDividerList>
            <InsightsDividerRow
              label="Has pour-over will"
              value="Yes"
              onClick={
                getCitationForField(citations, "pour_over_will.has_pour_over_will")
                  ? () => handleFieldClick("pour_over_will.has_pour_over_will")
                  : undefined
              }
            />
            {pour_over_will?.testator_name && (
              <InsightsDividerRow
                label="Testator"
                value={pour_over_will.testator_name}
                onClick={
                  getCitationForField(citations, "pour_over_will.testator_name")
                    ? () => handleFieldClick("pour_over_will.testator_name")
                    : undefined
                }
              />
            )}
            {pour_over_will?.will_date && (
              <InsightsDividerRow
                label="Will date"
                value={formatDate(pour_over_will.will_date)}
                onClick={
                  getCitationForField(citations, "pour_over_will.will_date")
                    ? () => handleFieldClick("pour_over_will.will_date")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}

      {hasAmendments && (
        <InsightsCard title="Amendments" noPadding>
          <ValidationWarnings warnings={warningsForSection("amendments")} />
          <div className="divide-y divide-stone-100">
            {(amendments ?? []).map((amendment, index) => (
              <div key={`amendment-${index}`} className="py-3 px-4 space-y-1.5">
                {amendment.amendment_date && (
                  <p className="text-sm font-medium text-primary">
                    {formatDate(amendment.amendment_date)}
                  </p>
                )}
                {amendment.amendment_scope && (
                  <p className="text-sm text-secondary">
                    {formatAmendmentScope(amendment.amendment_scope)}
                  </p>
                )}
                {amendment.description && (
                  <p className="text-sm text-secondary">{amendment.description}</p>
                )}
              </div>
            ))}
          </div>
        </InsightsCard>
      )}
    </div>
  );
};
